import React from 'react';
import { motion } from 'framer-motion';
import {
  Check, Sparkles, Command, Activity, Cpu
} from 'lucide-react';

const aiFeatures = [
  "AI Insurance Verification",
  "AI Claim Submission",
  "AI Patient Billing",
  "AI Denial Management",
  "AI EOB Posting",
  "AI AR & Collections",
  "AI Insurance Calling",
  "AI Payment Reconciliation",
  "AI Receptionist",
  "AI Hygiene Recall",
  "AI Case Acceptance",
  "AI Claim Scrubbing",
  "AI Schedule Optimizer",
  "AI Patient Intake",
  "AI Practice Manager",
  "AI Patient Experience",
  "AI Copilot",
  "AI Scribe",
  "AI Treatment Planner",
  "AI Revenue Analytics",
];


const MobileAdV4HeroSection = () => {
  return (
    <section className="relative overflow-hidden min-h-screen pb-16" style={{ background: 'linear-gradient(180deg, #1153E7 0%, #3A8BF9 100%)' }}>
      {/* Animated Light mode background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Animated Orbs */}
        <motion.div 
          className="absolute top-[-10%] left-[-10%] w-[60%] h-[60%] bg-white/20 blur-[120px] rounded-full mix-blend-overlay"
          animate={{ 
            scale: [1, 1.1, 1],
            opacity: [0.5, 0.8, 0.5],
            x: [0, 30, 0],
            y: [0, -20, 0]
          }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute top-[20%] right-[-10%] w-[50%] h-[50%] bg-blue-200/30 blur-[120px] rounded-full mix-blend-overlay"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.4, 0.7, 0.4],
            x: [0, -40, 0],
            y: [0, 30, 0]
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        />
        <motion.div 
          className="absolute bottom-[10%] left-[20%] w-[40%] h-[40%] bg-white/10 blur-[120px] rounded-full mix-blend-overlay"
          animate={{ 
            scale: [1, 1.15, 1],
            opacity: [0.3, 0.6, 0.3],
            x: [0, 20, 0],
            y: [0, -30, 0]
          }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut", delay: 5 }}
        />

        {/* Light streaks */}
        <motion.div
          className="absolute top-[5%] left-[10%] w-[400px] h-[600px] opacity-[0.07]"
          style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.8) 0%, transparent 100%)', transform: 'rotate(-15deg)', filter: 'blur(40px)' }}
          animate={{ opacity: [0.07, 0.15, 0.07], x: [0, 20, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute top-[10%] right-[5%] w-[300px] h-[500px] opacity-[0.05]"
          style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.6) 0%, transparent 100%)', transform: 'rotate(20deg)', filter: 'blur(50px)' }}
          animate={{ opacity: [0.05, 0.12, 0.05], x: [0, -15, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        />

        {/* Floating particles */}
        <motion.div className="absolute top-[15%] left-[20%] w-3 h-3 rounded-full bg-white/30" animate={{ y: [0, -30, 0], opacity: [0.2, 0.7, 0.2] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }} />
        <motion.div className="absolute top-[25%] right-[25%] w-2.5 h-2.5 rounded-full bg-white/20" animate={{ y: [0, -25, 0], opacity: [0.15, 0.5, 0.15] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }} />
        <motion.div className="absolute top-[10%] right-[15%] w-2 h-2 rounded-full bg-white/30" animate={{ y: [0, -35, 0], opacity: [0.2, 0.8, 0.2] }} transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }} />
        <motion.div className="absolute bottom-[30%] left-[10%] w-3 h-3 rounded-full bg-white/20" animate={{ y: [0, -20, 0], opacity: [0.2, 0.6, 0.2] }} transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 2 }} />
      </div>

      <div className="relative z-10 max-w-2xl mx-auto px-5 pt-16 pb-6">
        {/* Asha Logo */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <img src="/assets/alogo.png" alt="Asha Dental" className="h-8 w-auto rounded-lg shadow-sm" />
          <span className="font-bold text-xl tracking-wide text-white drop-shadow-sm">Asha Dental</span>
        </div>

        {/* Hero Headline */}
        <div className="text-center mb-8">
          <h1 className="text-[2.5rem] sm:text-6xl font-black leading-[1.05] tracking-[-0.03em] text-white mb-4 drop-shadow-md">
            The 1st{' '}
            <span className="relative inline-block">
              AI Native
              <div className="absolute -bottom-1 left-0 right-0 h-1.5 rounded-full bg-white/40" />
            </span>
            {' '}PMS
          </h1>
          <p className="text-lg sm:text-2xl text-white/90 font-medium max-w-lg mx-auto leading-snug drop-shadow-sm">
            Run your entire practice on autopilot, with 20 dental AI products in 1 platform.
          </p>
        </div>

        {/* AI Features checklist */}
        <div className="mb-10">
          <div className="grid grid-cols-2 gap-x-2 gap-y-3">
            {aiFeatures.map((feature, i) => (
              <div key={i} className="flex items-center gap-2.5">
                <div className="w-5 h-5 rounded-full bg-[#10b981] flex items-center justify-center shrink-0 shadow-sm">
                  <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                </div>
                <span className="text-sm sm:text-base text-white font-bold tracking-wide drop-shadow-sm">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Clean, contained prompt box & agents (No overflow, no browser graphic) */}
        <div className="relative w-full max-w-2xl mx-auto mt-4">
          
          {/* Central Glowing Prompt */}
          <div className="relative mb-6 group mx-auto">
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-300 via-indigo-300 to-purple-300 rounded-2xl blur-md opacity-60 group-hover:opacity-100 transition duration-1000 animate-pulse" />
            <div className="relative flex items-center gap-3 sm:gap-4 bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 shadow-xl">
              <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center shrink-0 border border-blue-100 shadow-sm">
                <Sparkles className="w-6 h-6 text-blue-500" />
              </div>
              <div className="flex-1 text-[15px] sm:text-xl text-slate-800 font-semibold leading-snug">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-slate-800 to-slate-600">
                  Hey Asha, take care of all my admin work for the next 2 days
                </span>
                <span className="inline-block w-2.5 h-5 sm:h-6 ml-2 align-middle bg-blue-500 animate-[pulse_1s_infinite] rounded-sm" />
              </div>
              <div className="hidden sm:flex px-3 py-1.5 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-500 items-center gap-1 font-mono shadow-sm shrink-0">
                <Command className="w-3 h-3" /> Enter
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default MobileAdV4HeroSection;
